import { jsPDF } from 'jspdf';
import { LOGO_BASE64 } from '@/utils/logoBase64';

// Color palette
const COLORS = {
  darkBlue: [0, 51, 102],
  mediumBlue: [0, 102, 153],
  lightBlue: [230, 242, 255],
  black: [0, 0, 0],
  darkGray: [51, 51, 51],
  mediumGray: [102, 102, 102],
  lightGray: [200, 200, 200],
  veryLightGray: [245, 245, 245],
  white: [255, 255, 255],
  accent: [0, 82, 136],
};

export const generateInvoicePDF = (invoiceData) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;

  // ============================================
  // HEADER SECTION - Logo + Company Info + Title
  // ============================================

  // Top accent line
  doc.setFillColor(...COLORS.darkBlue);
  doc.rect(0, 0, pageWidth, 3, 'F');

  // Add logo image
  try {
    doc.addImage(LOGO_BASE64, 'PNG', margin, 8, 22, 22);
  } catch (e) {
    // Fallback: draw circle if logo fails
    doc.setDrawColor(...COLORS.darkBlue);
    doc.setLineWidth(1.5);
    doc.circle(margin + 11, 19, 11);
    doc.setFontSize(6);
    doc.setFont('helvetica', 'bold');
    doc.text('LOGO', margin + 6, 20);
  }

  // Company name
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.darkBlue);
  doc.text('KOPERASI KONSUMEN TOENGGAK SEMI', margin + 26, 14);
  doc.text('MAKMUR SEGORO AGUNG', margin + 26, 20);

  // Company address
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.mediumGray);
  doc.text('Jl. Syeh Jumadil Kubro, Sentonorejo, Trowulan, Mojokerto, Jawa Timur', margin + 26, 26);

  // Invoice title box - right side
  doc.setFillColor(...COLORS.darkBlue);
  doc.roundedRect(pageWidth - margin - 55, 8, 55, 22, 2, 2, 'F');
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.white);
  doc.text('INVOICE', pageWidth - margin - 28, 18, { align: 'center' });
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text(invoiceData.invoiceNumber, pageWidth - margin - 28, 25, { align: 'center' });

  // Divider line
  doc.setDrawColor(...COLORS.lightGray);
  doc.setLineWidth(0.5);
  doc.line(margin, 35, pageWidth - margin, 35);

  // ============================================
  // INVOICE DETAILS - Date + Customer
  // ============================================

  // Date section
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.darkBlue);
  doc.text('Date:', pageWidth - margin - 55, 42);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.darkGray);
  const dateObj = new Date(invoiceData.invoiceDate);
  const formattedDate = `${String(dateObj.getDate()).padStart(2, '0')}/${String(dateObj.getMonth() + 1).padStart(2, '0')}/${dateObj.getFullYear()}`;
  doc.text(formattedDate, pageWidth - margin - 38, 42);

  // Customer info box
  doc.setFillColor(...COLORS.veryLightGray);
  doc.roundedRect(margin, 38, contentWidth * 0.6, 30, 2, 2, 'F');

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.darkBlue);
  doc.text('To:', margin + 4, 44);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.darkGray);
  doc.setFontSize(10);
  doc.text(invoiceData.customerName || '-', margin + 4, 50);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.darkBlue);
  doc.text('Address:', margin + 4, 56);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.darkGray);
  const addressLines = doc.splitTextToSize(invoiceData.customerAddress || '-', contentWidth * 0.55);
  doc.text(addressLines, margin + 4, 61);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.darkBlue);
  doc.text('Phone:', margin + 4, addressLines.length > 1 ? 67 + (addressLines.length - 1) * 4 : 67);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.darkGray);
  doc.text(invoiceData.customerPhone || '-', margin + 22, addressLines.length > 1 ? 67 + (addressLines.length - 1) * 4 : 67);

  // ============================================
  // TABLE SECTION
  // ============================================
  const tableStartY = 75;
  const headerHeight = 10;
  const rowHeight = 8;
  const colWidths = [12, 55, 22, 20, 30, 31]; // NO, Desc, Qty, Satuan, Harga, Total
  const colX = [margin];
  for (let i = 1; i < colWidths.length; i++) {
    colX.push(colX[i - 1] + colWidths[i - 1]);
  }
  const tableEndX = colX[colX.length - 1] + colWidths[colWidths.length - 1];

  // Table header background
  doc.setFillColor(...COLORS.darkBlue);
  doc.rect(margin, tableStartY, contentWidth, headerHeight, 'F');

  // Header text
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.white);
  const headers = ['NO', 'Description', 'Quantity', 'Satuan', 'Harga (Rp.)', 'Total'];
  headers.forEach((h, i) => {
    const cellCenter = colX[i] + colWidths[i] / 2;
    doc.text(h, cellCenter, tableStartY + 7, { align: 'center' });
  });

  // Table rows
  let currentY = tableStartY + headerHeight;
  doc.setTextColor(...COLORS.darkGray);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  const totalRows = Math.max(invoiceData.items.length, 8);

  for (let i = 0; i < totalRows; i++) {
    // Alternating row colors
    if (i % 2 === 0) {
      doc.setFillColor(...COLORS.veryLightGray);
    } else {
      doc.setFillColor(...COLORS.white);
    }
    doc.rect(margin, currentY, contentWidth, rowHeight, 'F');

    // Row border bottom
    doc.setDrawColor(...COLORS.lightGray);
    doc.setLineWidth(0.2);
    doc.line(margin, currentY + rowHeight, tableEndX, currentY + rowHeight);

    if (i < invoiceData.items.length) {
      const item = invoiceData.items[i];
      doc.setTextColor(...COLORS.darkGray);

      // NO - center aligned
      doc.text(String(item.no), colX[0] + colWidths[0] / 2, currentY + 5.5, { align: 'center' });
      // Description - left aligned with padding
      doc.text(item.description.substring(0, 28), colX[1] + 3, currentY + 5.5);
      // Quantity - center
      doc.text(String(item.quantity), colX[2] + colWidths[2] / 2, currentY + 5.5, { align: 'center' });
      // Satuan - center
      doc.text(item.unit, colX[3] + colWidths[3] / 2, currentY + 5.5, { align: 'center' });
      // Harga - right aligned
      doc.text(formatNumber(item.price), colX[4] + colWidths[4] - 3, currentY + 5.5, { align: 'right' });
      // Total - right aligned
      doc.text(formatNumber(item.total), colX[5] + colWidths[5] - 3, currentY + 5.5, { align: 'right' });
    }

    currentY += rowHeight;
  }

  // Table outer border
  doc.setDrawColor(...COLORS.darkBlue);
  doc.setLineWidth(0.5);
  doc.rect(margin, tableStartY, contentWidth, currentY - tableStartY);

  // Column vertical lines
  doc.setDrawColor(...COLORS.lightGray);
  doc.setLineWidth(0.2);
  colX.forEach((x, i) => {
    if (i > 0) {
      doc.line(x, tableStartY + headerHeight, x, currentY);
    }
  });

  // ============================================
  // TOTAL SECTION
  // ============================================
  currentY += 2;

  // Total box
  doc.setFillColor(...COLORS.darkBlue);
  doc.roundedRect(pageWidth - margin - 62, currentY, 62, 12, 2, 2, 'F');
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.white);
  doc.text('TOTAL', pageWidth - margin - 58, currentY + 8);
  doc.text(`Rp. ${formatNumber(invoiceData.grandTotal)}`, pageWidth - margin - 4, currentY + 8, { align: 'right' });

  // ============================================
  // REMARKS & BANK INFO
  // ============================================
  currentY += 20;

  // Remark section with accent border
  doc.setFillColor(...COLORS.lightBlue);
  doc.roundedRect(margin, currentY, contentWidth, 25, 2, 2, 'F');
  doc.setFillColor(...COLORS.darkBlue);
  doc.rect(margin, currentY, 3, 25, 'F');

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.darkBlue);
  doc.text('Remark:', margin + 6, currentY + 6);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.darkGray);
  doc.text('Pembayaran dilakukan dengan transfer ke Bank Account', margin + 6, currentY + 12);

  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.darkBlue);
  doc.text('Bank Account Detail:', margin + 6, currentY + 18);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.darkGray);
  doc.text('BRI an Koperasi Konsumen Toenggak Semi Makmur   005501004126566', margin + 45, currentY + 18);

  // ============================================
  // SIGNATURE SECTION
  // ============================================
  currentY += 35;

  // Left signature
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.darkBlue);
  doc.text('Penerima', margin + 25, currentY, { align: 'center' });

  doc.setDrawColor(...COLORS.lightGray);
  doc.setLineWidth(0.5);
  doc.line(margin + 5, currentY + 25, margin + 45, currentY + 25);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.mediumGray);
  doc.text('( ______________________ )', margin + 25, currentY + 30, { align: 'center' });

  // Right signature
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.darkBlue);
  doc.text('Pengirim', pageWidth - margin - 25, currentY, { align: 'center' });

  doc.setDrawColor(...COLORS.lightGray);
  doc.setLineWidth(0.5);
  doc.line(pageWidth - margin - 45, currentY + 25, pageWidth - margin - 5, currentY + 25);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.mediumGray);
  doc.text('( ______________________ )', pageWidth - margin - 25, currentY + 30, { align: 'center' });

  // ============================================
  // FOOTER
  // ============================================
  const footerY = doc.internal.pageSize.getHeight() - 10;

  // Footer line
  doc.setDrawColor(...COLORS.darkBlue);
  doc.setLineWidth(0.5);
  doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);

  doc.setFontSize(7);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...COLORS.mediumGray);
  doc.text('Koperasi Konsumen Toenggak Semi Makmur Segoro Agung | Jl. Syeh Jumadil Kubro, Sentonorejo, Trowulan, Mojokerto, Jawa Timur',
    pageWidth / 2, footerY, { align: 'center' });

  // Save PDF
  const fileName = `Invoice_${invoiceData.invoiceNumber.replace(/\//g, '_')}.pdf`;
  doc.save(fileName);
};

// Helper function to format numbers Indonesian style
const formatNumber = (num) => {
  return new Intl.NumberFormat('id-ID').format(num);
};
